
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building, Eye, ArrowRight, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

interface ClaimedNursingHomesProps {
  homes: any[];
  isLoading: boolean;
  error: Error | null;
}

export function ClaimedNursingHomes({ homes, isLoading, error }: ClaimedNursingHomesProps) {
  console.log("ClaimedNursingHomes received homes:", homes);
  
  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  if (!homes || homes.length === 0) {
    return <EmptyState />;
  }

  return (
    <Card>
      <CardHeader className="border-b border-gray-100">
        <CardTitle className="text-lg flex items-center gap-2">
          <Building className="h-5 w-5 text-grandpal-green-dark" />
          Your Claimed Nursing Homes
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-gray-100">
          {homes.map((home) => (
            <div key={home.nh_slug} className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-lg mb-1">{home.center_name}</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    {home.town}, {home.county}
                  </p>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      Verified Owner
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/nursing-homes/${home.county_slug}/${home.nh_slug}`}>
                      <Eye className="h-4 w-4 mr-2" />
                      View Profile
                    </Link>
                  </Button>
                  <Button asChild size="sm" className="bg-grandpal-green-dark hover:bg-grandpal-green-darkHover">
                    <Link to={`/owner/nursing-homes/${home.nh_slug}`}>
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function LoadingState() {
  return (
    <Card>
      <CardHeader className="border-b border-gray-100">
        <CardTitle className="text-lg flex items-center gap-2">
          <Building className="h-5 w-5 text-grandpal-green-dark" />
          Your Claimed Nursing Homes
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-900" />
        </div>
      </CardContent>
    </Card>
  );
}

function ErrorState({ error }: { error: Error }) {
  return (
    <Card>
      <CardHeader className="border-b border-gray-100">
        <CardTitle className="text-lg flex items-center gap-2">
          <Building className="h-5 w-5 text-grandpal-green-dark" />
          Your Claimed Nursing Homes
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="flex flex-col items-center justify-center text-center">
          <AlertCircle className="h-10 w-10 text-red-500 mb-4" />
          <h3 className="font-semibold text-lg mb-1">Error Loading Data</h3>
          <p className="text-muted-foreground mb-4">{error.message || "An unexpected error occurred"}</p>
          <Button onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState() {
  return (
    <Card>
      <CardHeader className="border-b border-gray-100">
        <CardTitle className="text-lg flex items-center gap-2">
          <Building className="h-5 w-5 text-grandpal-green-dark" />
          Your Claimed Nursing Homes
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="flex flex-col items-center justify-center text-center">
          <Building className="h-12 w-12 text-gray-300 mb-4" />
          <h3 className="font-semibold text-lg mb-1">No Claimed Nursing Homes Yet</h3>
          <p className="text-muted-foreground mb-4">
            You haven't claimed ownership of any nursing homes yet. Start by finding your nursing home and submitting a claim.
          </p>
          <Button asChild className="bg-grandpal-green-dark hover:bg-grandpal-green-darkHover">
            <Link to="/nursing-homes">
              Find Your Nursing Home
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
